import React, { forwardRef, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { clone } from "lodash";
import { Icon, theme } from "uikit";

import { StyledRow } from "../common";
import ColumnSetupModal from "../ColumnSetupModal";

import { Button } from "./components";

// forwardRef: a plain `ref` prop never reaches a function component, the tables
// measure this bar to size their horizontal scrollbar
const TableSettings = forwardRef<HTMLDivElement, TableSettings.Props>(
	(
		{
			value,
			defaultValue,
			columns,
			disabledColumnsIds = [],
			onChange,
			rightButton,
		},
		ref,
	) => {
		const { t } = useTranslation();
		const [showColumnModal, setShowColumnModal] = useState(false);

		const resetButtonOnClick = useCallback(
			() => defaultValue && onChange?.(clone(defaultValue)),
			[defaultValue, onChange],
		);
		const editButtonOnClick = useCallback(
			() => setShowColumnModal(true),
			[],
		);

		const columnSetupModalOnClose = useCallback(
			() => setShowColumnModal(false),
			[],
		);

		const columnSetupModalOnSubmit = useCallback(
			(columnSetupModalValue: ColumnSetupModal.Value) => {
				onChange?.(columnSetupModalValue);
				setShowColumnModal(false);
			},
			[onChange],
		);

		return (
			<>
				<StyledRow
					ref={ref}
					alignItems="center"
					gap="16px"
					p="7px 16px"
					bgC={theme.colors.button_secondary}
				>
					<Button
						icon="reset"
						label={t("tableSettings.str0") ?? ""}
						onClick={resetButtonOnClick}
					/>
					<Button
						icon="table-edit"
						label={t("tableSettings.str1") ?? ""}
						onClick={editButtonOnClick}
					/>

					{rightButton && (
						<div style={{ marginLeft: "auto" }}>
							<Button
								icon={rightButton.icon}
								label={rightButton.text ?? ""}
								onClick={rightButton.onClick}
								disabled={rightButton.disabled}
							/>
						</div>
					)}
				</StyledRow>

				{showColumnModal && (
					<ColumnSetupModal
						columns={columns}
						disabledColumns={disabledColumnsIds}
						value={value}
						onClose={columnSetupModalOnClose}
						onSubmit={columnSetupModalOnSubmit}
					/>
				)}
			</>
		);
	},
);

declare namespace TableSettings {
	type Column = ColumnSetupModal.Column;
	type Value = ColumnSetupModal.Value;

	interface Props {
		value?: Value;

		defaultValue?: Value;
		columns?: Column[];
		disabledColumnsIds?: Column["id"][];

		onChange?: any;

		rightButton?: {
			icon?: Icon.Props["id"];
			text?: string;
			onClick?: () => void;
			disabled?: boolean;
		};
	}
}

export default TableSettings;
